import { fetchApi } from "@/lib/api";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { z } from "zod";

export const MAX_MENTOR_METRICS_SORTS = 3;

const MONTH_FORMAT = "yyyy-MM";

export const mentorMetricsPeriodSchema = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/);

export const getCurrentMentorMetricsMonth = () =>
  DateTime.now().toFormat(MONTH_FORMAT);

export const usesRolling30DayMetrics = (period: MentorMetricsPeriod) =>
  period === getCurrentMentorMetricsMonth();

export const mentorMetricsSortFieldSchema = z.enum([
  "mentor_name",
  "tenure",
  "appeal_score",
  "active_caseload",
  "average_response_time",
  "d3_retention",
]);

export const mentorMetricsSortDirectionSchema = z.enum(["asc", "desc"]);

export const mentorMetricsSortSchema = z.object({
  field: mentorMetricsSortFieldSchema,
  direction: mentorMetricsSortDirectionSchema,
});

export type MentorMetricsSort = z.infer<typeof mentorMetricsSortSchema>;
export type MentorMetricsSortField = z.infer<
  typeof mentorMetricsSortFieldSchema
>;

export const DEFAULT_MENTOR_METRICS_SORT: MentorMetricsSort[] = [
  { field: "tenure", direction: "asc" },
];

const trendDirectionSchema = z.enum(["up", "down", "unchanged", "unavailable"]);

const metricTrendSchema = z.object({
  currentWeekValue: z.number().nullable(),
  previousWeekValue: z.number().nullable(),
  absoluteChange: z.number().nullable(),
  direction: trendDirectionSchema,
});

const summaryMetricSchema = z.object({
  value: z.number().nullable(),
  trend: metricTrendSchema,
});

// a percentage, not a 0-1 fraction; the bound makes a scale change fail loudly
const percentageSummaryMetricSchema = summaryMetricSchema.extend({
  value: z.number().min(0).max(100).nullable(),
});

export const mentorMetricsSummarySchema = z.object({
  period: z.object({
    startDate: z.string(),
    endDateExclusive: z.string(),
  }),
  availableMonths: z.array(z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/)),
  appealScoreAvailable: z.boolean(),
  totalMentors: summaryMetricSchema,
  averageTenureMonths: summaryMetricSchema,
  averageAppealScore: summaryMetricSchema,
  averageEngagedTeens: z.number().nullable(),
  averageActiveCaseload: summaryMetricSchema,
  averageResponseTimeSeconds: summaryMetricSchema,
  averageD3RetentionRate: percentageSummaryMetricSchema,
});

export const mentorMetricsRowSchema = z.object({
  mentorId: z.string().uuid(),
  mentorName: z.string(),
  profileImageUrl: z.string().nullable(),
  onboardedAt: z.string(),
  tenureDays: z.number().nonnegative(),
  tenureMonths: z.number().nonnegative(),
  appealScore: z.number().nullable(),
  appealAppearances: z.number().nonnegative(),
  appealSelections: z.number().nonnegative(),
  appealReliable: z.boolean(),
  engagedTeens: z.number().nonnegative().nullable(),
  activeCaseload: z.number().nonnegative().nullable(),
  averageResponseTimeSeconds: z.number().nonnegative().nullable(),
  d3RetentionRate: z.number().nonnegative().max(100).nullable(),
  d3CohortSize: z.number().nonnegative(),
  d3Returned: z.number().nonnegative(),
  d3Reliable: z.boolean(),
});

export const mentorMetricsTablePageSchema = z.object({
  period: z.object({
    startDate: z.string(),
    endDateExclusive: z.string(),
  }),
  appealScoreAvailable: z.boolean(),
  rows: z.array(mentorMetricsRowSchema),
  pagination: z.object({
    page: z.number().positive(),
    pageSize: z.number().positive(),
    total: z.number().nonnegative(),
    totalPages: z.number().nonnegative(),
  }),
});

export type MentorMetricsPeriod = z.infer<typeof mentorMetricsPeriodSchema>;
export type MentorMetricsRequestPeriod = MentorMetricsPeriod | "last_30_days";
export type MentorMetricsSummary = z.infer<typeof mentorMetricsSummarySchema>;
export type MentorMetricsRow = z.infer<typeof mentorMetricsRowSchema>;
export type MentorMetricsTablePage = z.infer<
  typeof mentorMetricsTablePageSchema
>;
export type MentorMetricsSummaryMetric = z.infer<typeof summaryMetricSchema>;

export const ROLLING_30_DAYS_PERIOD = "last_30_days" as const;

export const ROLLING_METRIC_SORT_FIELDS: MentorMetricsSortField[] = [
  "appeal_score",
  "average_response_time",
  "d3_retention",
];

export const isRollingMetricSort = (sort: MentorMetricsSort[]) =>
  ROLLING_METRIC_SORT_FIELDS.includes(sort[0]?.field);

export interface MentorMetricsScopeParams {
  period: MentorMetricsRequestPeriod;
  leadMentorIds: string[];
}

export interface MentorMetricsTableParams extends MentorMetricsScopeParams {
  search: string;
  sort: MentorMetricsSort[];
  page: number;
  pageSize: number;
}

const normalizeSort = (sorts: MentorMetricsSort[]): MentorMetricsSort[] => {
  if (sorts.length === 0) return DEFAULT_MENTOR_METRICS_SORT;

  const appealPrimary = sorts.find((sort) => sort.field === "appeal_score");
  if (appealPrimary) {
    const nameSecondary = sorts.find((sort) => sort.field === "mentor_name");
    return nameSecondary ? [appealPrimary, nameSecondary] : [appealPrimary];
  }

  return sorts.slice(0, MAX_MENTOR_METRICS_SORTS);
};

export const parseMentorMetricsSort = (
  sortValue: string,
): MentorMetricsSort[] => {
  if (sortValue.trim() === "") return DEFAULT_MENTOR_METRICS_SORT;

  const parsed = sortValue
    .split(",")
    .map((part) => {
      const [field, direction] = part.split(":");
      const parsedField = mentorMetricsSortFieldSchema.safeParse(field);
      const parsedDirection =
        mentorMetricsSortDirectionSchema.safeParse(direction);
      if (!parsedField.success || !parsedDirection.success) return null;
      return {
        field: parsedField.data,
        direction: parsedDirection.data,
      };
    })
    .filter((sort): sort is MentorMetricsSort => sort !== null);

  return normalizeSort(parsed);
};

export const nextMentorMetricsSort = (
  current: MentorMetricsSort[],
  field: MentorMetricsSortField,
): MentorMetricsSort[] => {
  const currentSort = current.find((item) => item.field === field);

  if (!currentSort) {
    if (field === "appeal_score") {
      return [{ field, direction: "asc" }];
    }
    if (current.some((item) => item.field === "appeal_score")) {
      if (field === "mentor_name") {
        const appeal = current.find((item) => item.field === "appeal_score");
        return appeal
          ? [appeal, { field, direction: "asc" }]
          : [{ field, direction: "asc" }];
      }
      return [{ field, direction: "asc" }];
    }
    return normalizeSort([
      ...current.slice(-(MAX_MENTOR_METRICS_SORTS - 1)),
      { field, direction: "asc" },
    ]);
  }

  if (currentSort.direction === "asc") {
    return current.map((item) =>
      item.field === field ? { ...item, direction: "desc" } : item,
    );
  }

  const cleared = current.filter((item) => item.field !== field);
  return cleared.length > 0 ? cleared : DEFAULT_MENTOR_METRICS_SORT;
};

const createScopeSearchParams = (params: MentorMetricsScopeParams) => {
  const searchParams = new URLSearchParams();

  if (params.period === ROLLING_30_DAYS_PERIOD) {
    searchParams.set("period", ROLLING_30_DAYS_PERIOD);
  } else {
    searchParams.set("period", "calendar_month");
    searchParams.set("month", params.period);
  }

  if (params.leadMentorIds.length > 0) {
    searchParams.set("leadMentorIds", params.leadMentorIds.join(","));
  }

  return searchParams;
};

const createTableSearchParams = (params: MentorMetricsTableParams) => {
  const searchParams = createScopeSearchParams(params);

  if (params.search) searchParams.set("search", params.search);
  searchParams.set(
    "sort",
    normalizeSort(params.sort)
      .map(({ field, direction }) => `${field}:${direction}`)
      .join(","),
  );
  searchParams.set("page", params.page.toString());
  searchParams.set("pageSize", params.pageSize.toString());

  return searchParams;
};

const fetchMentorMetricsTablePage = async (
  params: MentorMetricsTableParams,
) => {
  const response = await fetchApi<MentorMetricsTablePage>({
    url: `/v2/mentor/metrics-dashboard/table?${createTableSearchParams(params)}`,
    method: "GET",
  });
  return mentorMetricsTablePageSchema.parse(response.data);
};

const OVERLAY_PAGE_SIZE = 100;

export const useMentorMetricsSummary = (
  params: MentorMetricsScopeParams,
  enabled = true,
) => {
  return useQuery({
    queryKey: ["mentor-metrics-dashboard", "summary", params],
    queryFn: async () => {
      const response = await fetchApi<MentorMetricsSummary>({
        url: `/v2/mentor/metrics-dashboard/summary?${createScopeSearchParams(params)}`,
        method: "GET",
      });
      return mentorMetricsSummarySchema.parse(response.data);
    },
    enabled,
    placeholderData: keepPreviousData,
  });
};

export const useMentorMetricsTable = (params: MentorMetricsTableParams) => {
  return useQuery({
    queryKey: ["mentor-metrics-dashboard", "table", params],
    queryFn: () => fetchMentorMetricsTablePage(params),
    placeholderData: keepPreviousData,
  });
};

export const useMentorMetricsTableOverlay = (
  params: Omit<MentorMetricsTableParams, "page" | "pageSize">,
  enabled = true,
) => {
  return useQuery({
    queryKey: ["mentor-metrics-dashboard", "table-overlay", params],
    enabled,
    queryFn: async () => {
      const first = await fetchMentorMetricsTablePage({
        ...params,
        page: 1,
        pageSize: OVERLAY_PAGE_SIZE,
      });
      const rows = [...first.rows];

      for (let page = 2; page <= first.pagination.totalPages; page += 1) {
        const next = await fetchMentorMetricsTablePage({
          ...params,
          page,
          pageSize: OVERLAY_PAGE_SIZE,
        });
        rows.push(...next.rows);
      }

      return {
        rows,
        appealScoreAvailable: first.appealScoreAvailable,
      };
    },
    placeholderData: keepPreviousData,
  });
};
